import React from "react";
import {
  RiAttachmentLine,
  RiErrorWarningLine,
  RiFlowChart,
  RiStackLine,
  RiTeamLine,
  RiUserLine,
} from "react-icons/ri";

function HowToUseStackformContent() {
  const isDarkMode = document.body.classList.contains('dark-mode');

  const tipBoxStyle = {
    backgroundColor: isDarkMode ? "#1e3a5f" : "#f0f9ff",
    border: isDarkMode ? "1px solid #3b82f6" : "1px solid #0284c7",
    borderRadius: "8px",
    padding: "1rem",
    marginTop: "1.5rem",
    marginBottom: "1.5rem",
  };

  const tipHeadingStyle = {
    marginTop: 0,
    color: isDarkMode ? "#60a5fa" : "#0284c7",
  };

  return (
    <div>
      <h2>Getting Started with Stackform</h2>
      <p>
        Stackform is a multi-agent AI platform built for research, discovery,
        and brainstorming. Here's what you need to know to get going in under
        five minutes.
      </p>

      <h3>Step 1 — Choose your workflow</h3>
      <p>
        Use the mode selector at the top of the chat panel to pick your
        workflow:
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ padding: '0.75rem 1rem', border: '1px solid var(--border-color, #ddd)', borderRadius: '8px' }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--primary-color, #636cff)' }}>
            <RiStackLine size={18} /> Ask (Orchestration)
          </strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            Best for research, factual questions, and anything that benefits
            from multiple sources. The Orchestrator gives you an immediate
            answer, then dispatches agents for deeper research, and synthesizes
            a final report. See <strong>Workflows</strong> for full details.
          </p>
        </div>

        <div style={{ padding: '0.75rem 1rem', border: '1px solid var(--border-color, #ddd)', borderRadius: '8px' }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--success-color, #28a745)' }}>
            <RiTeamLine size={18} /> Swarm (Open Forum)
          </strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            Best for brainstorming, exploring competing perspectives, and
            creative exploration. Multiple agents debate your topic in real
            time; you can intervene, pause, and steer at any point. See{" "}
            <strong>Workflows</strong> for full details.
          </p>
        </div>

        <div style={{ padding: '0.75rem 1rem', border: '1px solid var(--border-color, #ddd)', borderRadius: '8px' }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#f59e0b' }}>
            <RiFlowChart size={18} /> Workflows (Pipelines)
          </strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            Best for repeatable, multi-step tasks. Select a workflow from the{' '}
            <strong>⚙ Workflows</strong> dropdown in the mode selector, type your
            input, and press <strong>Run</strong>. Five platform workflows are
            built in (Tool to Agent, Agent Only, Route by Score, Research
            Brief, Parallel Perspectives), and you can build unlimited custom
            pipelines in the{' '}
            <strong>Workflow Builder</strong> (open it from the{' '}
            <strong>Workflows</strong> menu at the top-right). See{' '}
            <strong>Workflows</strong> in the help menu for full details.
          </p>
        </div>
      </div>

      <h3>Step 2 — Write your query</h3>
      <p>
        Type your question or topic in the input box. A few tips to get
        better results faster:
      </p>
      <ul style={{ marginBottom: '1rem' }}>
        <li>
          <strong>Be specific:</strong> "Explain transformer architecture for
          a software engineer" works better than "explain AI."
        </li>
        <li>
          <strong>Direct the agent when you know what you need:</strong> "Use
          the researcher agent to find papers on CRISPR off-target effects."
        </li>
        <li>
          <strong>Control depth:</strong> Add "quick answer" for a fast
          Orchestrator-only response, or use <strong>Expansive</strong> on
          any response for the deepest analysis. See{" "}
          <strong>Prompting 101</strong> for the full guide.
        </li>
      </ul>

      <h3>Step 3 — Use on-demand response options</h3>
      <p>
        Every response in Ask mode has three on-demand actions you can
        trigger at any time:
      </p>
      <ul style={{ marginBottom: '1rem' }}>
        <li><strong>Concise:</strong> Shows the Orchestrator's fast preliminary answer.</li>
        <li><strong>Expansive:</strong> Re-runs the full agentic deep-dive for maximum depth.</li>
        <li><strong>Evaluation:</strong> Asks a dedicated Evaluator agent to audit the response for accuracy and completeness.</li>
      </ul>

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        backgroundColor: isDarkMode ? '#2d2310' : '#fffbeb',
        border: `1px solid ${isDarkMode ? '#b45309' : '#f59e0b'}`,
        borderRadius: '8px',
        padding: '0.85rem 1rem',
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <RiErrorWarningLine size={20} style={{ color: isDarkMode ? '#fbbf24' : '#b45309', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.9rem' }}>
          <strong style={{ color: isDarkMode ? '#fbbf24' : '#b45309' }}>Running sessions concurrently</strong>
          <p style={{ margin: '0.35rem 0 0' }}>
            You can run multiple Ask and Swarm sessions at the same time —
            all process in the background and survive browser restarts.
            Keep in mind that each active session draws from your provider
            simultaneously, so running many at once may hit your plan's
            rate or quota limits (RPM / TPM). See <strong>Workflows → Concurrent Sessions</strong> for details.
          </p>
        </div>
      </div>

      <div style={tipBoxStyle}>
        <h4 style={tipHeadingStyle}>
          <RiAttachmentLine style={{ verticalAlign: "middle", marginRight: "6px" }} />
          Attach files to any query
        </h4>
        <p style={{ margin: 0 }}>
          Click the attachment icon (<RiAttachmentLine style={{ verticalAlign: "middle" }} />) to
          upload files before submitting. Supported formats:
        </p>
        <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          <li><strong>Images:</strong> PNG, JPG/JPEG, WEBP — analyze charts, photos, or diagrams.</li>
          <li><strong>Documents:</strong> PDF, TXT — summarize reports, extract data from papers.</li>
        </ul>
      </div>

      <h3>
        <RiUserLine style={{ verticalAlign: "middle", marginRight: "6px" }} />
        Personas — shape how agents respond
      </h3>
      <p>
        A <strong>Persona</strong> is a saved system prompt that gives an agent
        a specific role, tone, or expertise. Assign a "financial analyst"
        persona to the Orchestrator and every response will reflect that lens —
        without you having to re-explain it each time.
      </p>
      <p>
        Stackform ships with three platform defaults — <strong>Analyst</strong>,{" "}
        <strong>Skeptic</strong>, and <strong>Mediator</strong> — designed to
        give each agent a distinct analytical stance during a Swarm debate.
        Open <strong>User Settings → Personas</strong> to manage them, then
        assign them to individual agents under{" "}
        <strong>User Settings → Model Defaults → Agent Models</strong>. See the{" "}
        <strong>Personas</strong> entry in this help menu for full descriptions
        and usage tips.
      </p>

      <h3>Make it your own — models &amp; agents</h3>
      <p>
        Open <strong>User Settings → Model Defaults</strong> to tailor which
        models power your agents:
      </p>
      <ul style={{ marginBottom: '1rem' }}>
        <li>
          <strong>Main model</strong> — the default model used across modes.
        </li>
        <li>
          <strong>Agent Models</strong> — give each subagent (Aeri, Capo, and
          Cardian) its own provider, model, and persona, or set its provider
          to <strong>None</strong> to disable it (run one or two agents instead
          of all three). You can also add your own custom agents.
        </li>
        <li>
          <strong>Summarizer Model</strong> (optional) — a dedicated model for
          synthesizing multi-agent reports in Workflows; leave it empty to reuse
          your main model.
        </li>
        <li>
          <strong>Model Profiles</strong> — save the whole setup above (main
          model, summarizer, and per-agent assignments) as named presets, such
          as a fast cloud profile and an all-local <strong>Private</strong> one.
          Create, duplicate, rename, and delete profiles here in{" "}
          <strong>Model Defaults</strong>; once saved, switch the active profile
          in a click from the model selector in the chat panel.
        </li>
        <li>
          <strong>Your own keys, or run locally</strong> — add provider keys
          under <strong>API Keys</strong>, or run agents on local models from
          your own device via the <strong>Connect</strong> tab (e.g. Ollama).
          See <strong>Use Your Own AI Models (BYOE)</strong> for the full guide.
        </li>
      </ul>

      <hr style={{ marginTop: "2rem" }} />

      <h3>Example prompts to try right now</h3>
      <p>
        <strong>Ask — research question:</strong><br />
        <em>"What are the most recent peer-reviewed findings on the long-term
        effects of intermittent fasting?"</em>
      </p>
      <p>
        <strong>Ask — current events:</strong><br />
        <em>"Summarize the latest developments in the US-China semiconductor
        trade dispute."</em>
      </p>
      <p>
        <strong>Swarm — brainstorm:</strong><br />
        <em>"Debate the pros and cons of a four-day work week."</em>
      </p>
      <p>
        <strong>Workflow — recurring research:</strong><br />
        <em>"What are the latest breakthroughs in fusion energy?"</em> (select{' '}
        <strong>Research Brief</strong> from the ⚙ Workflows dropdown first)
      </p>
      <p>
        <strong>With attachment — document analysis:</strong><br />
        <em>"Analyze this report and identify the three biggest financial risks
        mentioned."</em> (upload a PDF first)
      </p>
    </div>
  );
}

export default HowToUseStackformContent;
